import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { ThemeToggle } from './theme-toggle';
import { UserMenu } from './UserMenu';
import { CreditBalanceIndicator } from '@/credits/components';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './ui/sheet';
import { useAuth } from '@/auth/hooks/useAuth';

interface HeaderProps {
  /**
   * When true, shows only logo + theme toggle.
   * Hides navigation links, user menu, and hamburger menu on mobile.
   * Used for public arches when user is not logged in.
   */
  minimal?: boolean;
}

export function Header({ minimal = false }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuthenticated } = useAuth();

  // Minimal header: only logo and theme toggle
  if (minimal) {
    return (
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <Link to="/">
                <img src="/fias-logo.png" alt="Fias" className="h-[42px] dark:invert" />
              </Link>
            </div>
            <div className="flex items-center">
              <ThemeToggle />
            </div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="border-b border-border bg-card">
      <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <Link to="/">
              <img src="/fias-logo.png" alt="Fias" className="h-[42px] dark:invert" />
            </Link>
          </div>

          {/* Desktop navigation */}
          <nav className="hidden lg:flex items-center space-x-4">
            {isAuthenticated && (
              <>
                <Link to="/a/arche_favorites" className="text-primary hover:underline">
                  Worktop
                </Link>
                <Link to="/a/arche_make_a_request" className="text-primary hover:underline">
                  Re-Quest
                </Link>
                <a
                  href="https://fias.io"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Fias.io
                </a>
                <Link to="/marketplace" className="text-primary hover:underline">
                  Arche Store
                </Link>
              </>
            )}
            <ThemeToggle />
            {isAuthenticated && <CreditBalanceIndicator />}
            <UserMenu />
          </nav>

          {/* Mobile navigation */}
          <div className="flex items-center space-x-2 lg:hidden">
            <ThemeToggle />
            {isAuthenticated && <CreditBalanceIndicator />}
            {isAuthenticated ? (
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Open menu">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[280px]">
                  <SheetHeader>
                    <SheetTitle>Menu</SheetTitle>
                  </SheetHeader>
                  <nav className="flex flex-col space-y-1 mt-6">
                    <Link
                      to="/a/arche_favorites"
                      className="flex items-center min-h-[44px] px-3 py-2 text-primary hover:bg-accent rounded-md"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Worktop
                    </Link>
                    <Link
                      to="/a/arche_make_a_request"
                      className="flex items-center min-h-[44px] px-3 py-2 text-primary hover:bg-accent rounded-md"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Re-Quest
                    </Link>
                    <a
                      href="https://fias.io"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center min-h-[44px] px-3 py-2 text-primary hover:bg-accent rounded-md"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Fias.io
                    </a>
                    <Link
                      to="/marketplace"
                      className="flex items-center min-h-[44px] px-3 py-2 text-primary hover:bg-accent rounded-md"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Arche Store
                    </Link>
                    <div className="border-t border-border my-2" />
                    <div className="px-3 py-2">
                      <UserMenu showAccountLabel />
                    </div>
                  </nav>
                </SheetContent>
              </Sheet>
            ) : (
              <UserMenu />
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
